import React from 'react';
import { Box, Paper, Typography, IconButton, Avatar, Slide } from '@mui/material';
import Close from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const VARIANT_META = {
  success: {
    title: 'Success',
    Icon: CheckCircle,
    avatarBg: 'success.main',
    borderColor: 'success.light',
  },
  error: {
    title: 'Failed',
    Icon: ErrorOutline,
    avatarBg: 'error.main',
    borderColor: 'error.light',
  },
  warning: {
    title: 'Warning',
    Icon: WarningAmber,
    avatarBg: 'warning.main',
    borderColor: 'warning.light',
  },
  info: {
    title: 'Info',
    Icon: InfoOutlined,
    avatarBg: 'info.main',
    borderColor: 'info.light',
  },
};

function formatTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Fixed stack of notifications on the right (success / error / warning / info).
 */
export default function PageToastStack({ toasts = [], onDismiss }) {
  return (
    <Box
        sx={{
        position: 'fixed',
        top: { xs: 'calc(104px + env(safe-area-inset-top, 0px))', sm: 80 },
        right: { xs: 12, sm: 24 },
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        maxWidth: 380,
        width: { xs: 'calc(100vw - 24px)', sm: 380 },
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {toasts.map((t) => {
        const severity = VARIANT_META[t.severity] ? t.severity : 'info';
        const meta = VARIANT_META[severity];
        const { Icon } = meta;
        return (
          <Slide in key={t.id} direction="left" timeout={240} mountOnEnter unmountOnExit>
            <Paper
              elevation={8}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.5,
                pr: 0.75,
                borderRadius: 2,
                borderLeftWidth: 4,
                borderLeftStyle: 'solid',
                borderLeftColor: meta.borderColor,
                bgcolor: 'background.paper',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 8px 24px rgba(0,0,0,0.45)'
                    : '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: meta.avatarBg,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 22, color: 'common.white' }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {meta.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatTime(t.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                  {t.message}
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Dismiss" onClick={() => onDismiss(t.id)} sx={{ mt: -0.25, color: 'text.secondary' }}>
                <Close fontSize="small" />
              </IconButton>
            </Paper>
          </Slide>
        );
      })}
    </Box>
  );
}
